import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import DOMPurify from 'dompurify';
import { CircleCheck, CircleAlert, Loader2, Mail } from 'lucide-react';

import { useAuth } from '../lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Field, FieldLabel, FieldError, FieldSeparator } from '@/components/ui/field';

// Zod schemas for form validation
const signInSchema = z.object({
  email: z.email('Please enter a valid email address'),
  // Sign-in only requires non-empty password (server validates credentials)
  // Sign-up enforces minimum length for new passwords
  password: z.string().min(1, 'Enter your password'),
});

const signUpSchema = z
  .object({
    email: z.email('Please enter a valid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

const magicLinkSchema = z.object({
  email: z.email('Please enter a valid email address'),
});

type SignInFormData = z.infer<typeof signInSchema>;
type SignUpFormData = z.infer<typeof signUpSchema>;

type AuthMode = 'signin' | 'signup';

// Sanitize URL error parameter to prevent XSS and limit length
function sanitizeErrorParam(error: string | undefined): string | null {
  if (!error) return null;
  const sanitized = DOMPurify.sanitize(error.slice(0, 200), { ALLOWED_TAGS: [] });
  return sanitized || null;
}

export function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { error?: string };
  const errorFromUrl = sanitizeErrorParam(search?.error);

  const [mode, setMode] = useState<AuthMode>('signin');
  const [error, setError] = useState<string | null>(errorFromUrl);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    signIn,
    signInPending,
    signUp,
    signUpPending,
    sendMagicLink,
    magicLinkPending,
    magicLinkSuccess,
    resetMagicLink,
    signInWithOAuth,
  } = useAuth();

  const isLoading = signInPending || signUpPending || magicLinkPending;

  // Sign In form
  const signInForm = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '', password: '' },
  });

  // Sign Up form
  const signUpForm = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { email: '', password: '', confirmPassword: '' },
  });

  const handleSignIn = async (data: SignInFormData) => {
    setError(null);
    setSuccessMessage(null);

    try {
      await signIn({ email: data.email, password: data.password });
      navigate({ to: '/' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleSignUp = async (data: SignUpFormData) => {
    setError(null);
    setSuccessMessage(null);

    try {
      await signUp({ email: data.email, password: data.password });
      // User is always present per the return type, so always navigate
      navigate({ to: '/' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleMagicLink = async () => {
    setError(null);
    setSuccessMessage(null);

    // Get email from the sign-in form
    const email = signInForm.getValues('email');

    // Validate email using the magic link schema
    const result = magicLinkSchema.safeParse({ email });
    if (!result.success) {
      setError('Please enter your email address');
      return;
    }

    try {
      const result = await sendMagicLink({ email });
      setSuccessMessage(result.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send magic link');
    }
  };

  const handleOAuth = (provider: 'google' | 'apple' | 'azure') => {
    signInWithOAuth(provider);
  };

  const handleTabChange = (value: string) => {
    // Preserve email when switching tabs for convenience
    const currentEmail =
      mode === 'signin' ? signInForm.getValues('email') : signUpForm.getValues('email');

    setMode(value as AuthMode);
    setError(null);
    setSuccessMessage(null);
    resetMagicLink();

    // Reset forms but keep the email
    signInForm.reset({ email: currentEmail, password: '' });
    signUpForm.reset({ email: currentEmail, password: '', confirmPassword: '' });
  };

  return (
    <div className="bg-muted flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 text-center">
          <h1 className="text-foreground text-3xl font-bold">Avatar</h1>
        </div>

        <Card>
          <CardHeader className="pb-0">
            <CardTitle className="sr-only">Authentication</CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Success Message */}
            {(successMessage || magicLinkSuccess) && (
              <Alert
                aria-live="polite"
                className="border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300"
              >
                <CircleCheck className="size-4" />
                <AlertDescription>
                  {successMessage || 'Check your email for the magic link!'}
                </AlertDescription>
              </Alert>
            )}

            {/* Error Message */}
            {error && (
              <Alert variant="destructive" aria-live="polite">
                <CircleAlert className="size-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Tabs value={mode} onValueChange={handleTabChange}>
              <TabsList variant="line" className="w-full">
                <TabsTrigger value="signin" className="flex-1">
                  Sign In
                </TabsTrigger>
                <TabsTrigger value="signup" className="flex-1">
                  Sign Up
                </TabsTrigger>
              </TabsList>

              {/* Sign In Form */}
              <TabsContent value="signin">
                <form onSubmit={signInForm.handleSubmit(handleSignIn)} className="space-y-4">
                  <Field data-invalid={!!signInForm.formState.errors.email}>
                    <FieldLabel htmlFor="signin-email">Email</FieldLabel>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="you@example.com"
                      autoComplete="email"
                      aria-invalid={!!signInForm.formState.errors.email}
                      aria-describedby={
                        signInForm.formState.errors.email ? 'signin-email-error' : undefined
                      }
                      {...signInForm.register('email')}
                    />
                    <FieldError
                      id="signin-email-error"
                      errors={[signInForm.formState.errors.email]}
                    />
                  </Field>

                  <Field data-invalid={!!signInForm.formState.errors.password}>
                    <FieldLabel htmlFor="signin-password">Password</FieldLabel>
                    <Input
                      id="signin-password"
                      type="password"
                      placeholder="********"
                      autoComplete="current-password"
                      aria-invalid={!!signInForm.formState.errors.password}
                      aria-describedby={
                        signInForm.formState.errors.password ? 'signin-password-error' : undefined
                      }
                      {...signInForm.register('password')}
                    />
                    <FieldError
                      id="signin-password-error"
                      errors={[signInForm.formState.errors.password]}
                    />
                  </Field>

                  <Button type="submit" disabled={isLoading} className="w-full">
                    {signInPending ? (
                      <>
                        <Loader2 className="animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="secondary"
                    disabled={isLoading}
                    onClick={handleMagicLink}
                    className="w-full"
                  >
                    {magicLinkPending ? (
                      <>
                        <Loader2 className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail />
                        Send Magic Link
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* Sign Up Form */}
              <TabsContent value="signup">
                <form onSubmit={signUpForm.handleSubmit(handleSignUp)} className="space-y-4">
                  <Field data-invalid={!!signUpForm.formState.errors.email}>
                    <FieldLabel htmlFor="signup-email">Email</FieldLabel>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      autoComplete="email"
                      aria-invalid={!!signUpForm.formState.errors.email}
                      aria-describedby={
                        signUpForm.formState.errors.email ? 'signup-email-error' : undefined
                      }
                      {...signUpForm.register('email')}
                    />
                    <FieldError
                      id="signup-email-error"
                      errors={[signUpForm.formState.errors.email]}
                    />
                  </Field>

                  <Field data-invalid={!!signUpForm.formState.errors.password}>
                    <FieldLabel htmlFor="signup-password">Password</FieldLabel>
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder="********"
                      autoComplete="new-password"
                      aria-invalid={!!signUpForm.formState.errors.password}
                      aria-describedby={
                        signUpForm.formState.errors.password ? 'signup-password-error' : undefined
                      }
                      {...signUpForm.register('password')}
                    />
                    <FieldError
                      id="signup-password-error"
                      errors={[signUpForm.formState.errors.password]}
                    />
                  </Field>

                  <Field data-invalid={!!signUpForm.formState.errors.confirmPassword}>
                    <FieldLabel htmlFor="signup-confirm-password">Confirm Password</FieldLabel>
                    <Input
                      id="signup-confirm-password"
                      type="password"
                      placeholder="********"
                      autoComplete="new-password"
                      aria-invalid={!!signUpForm.formState.errors.confirmPassword}
                      aria-describedby={
                        signUpForm.formState.errors.confirmPassword
                          ? 'signup-confirm-password-error'
                          : undefined
                      }
                      {...signUpForm.register('confirmPassword')}
                    />
                    <FieldError
                      id="signup-confirm-password-error"
                      errors={[signUpForm.formState.errors.confirmPassword]}
                    />
                  </Field>

                  <Button type="submit" disabled={isLoading} className="w-full">
                    {signUpPending ? (
                      <>
                        <Loader2 className="animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            {/* Divider */}
            <FieldSeparator>or</FieldSeparator>

            {/* OAuth Buttons */}
            <div className="space-y-3">
              <Button
                type="button"
                variant="outline"
                disabled={isLoading}
                onClick={() => handleOAuth('google')}
                className="w-full"
              >
                <GoogleIcon />
                Continue with Google
              </Button>

              <Button
                type="button"
                variant="outline"
                disabled={isLoading}
                onClick={() => handleOAuth('apple')}
                className="w-full"
              >
                <AppleIcon />
                Continue with Apple
              </Button>

              <Button
                type="button"
                variant="outline"
                disabled={isLoading}
                onClick={() => handleOAuth('azure')}
                className="w-full"
              >
                <MicrosoftIcon />
                Continue with Microsoft
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Custom OAuth icons with brand colors
function GoogleIcon() {
  return (
    <svg className="size-5" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg className="size-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg className="size-5" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#00A4EF" d="M1 13h10v10H1z" />
      <path fill="#7FBA00" d="M13 1h10v10H13z" />
      <path fill="#FFB900" d="M13 13h10v10H13z" />
    </svg>
  );
}
